/* TWE custom scrollbar — fixed-height thumb.
 *
 * Native thumbs are sized by the browser from the content ratio; their length
 * cannot be set in CSS. To get a constant thumb we hide the native bar and
 * draw our own.
 *
 * Positioning note (this is what keeps it smooth): the thumb is appended to
 * <body> as position:fixed rather than living inside the scroller. An
 * absolutely-positioned child of a scroll container is moved by the compositor
 * as the container scrolls, and JS then has to drag it back into place every
 * frame. Those two fight, and on compositor-driven scrolling (trackpad,
 * momentum) the correction lands a frame late, which reads as jitter. Fixed
 * positioning means only this script ever moves the thumb.
 *
 * Progressive enhancement: the native scrollbar is only hidden once this
 * script attaches (it adds .twe-has-custom-scrollbar). If the file fails to
 * load, the CSS-styled native scrollbar remains.
 */
(function () {
  'use strict';

  var THUMB_HEIGHT = 350; // px — keep in sync with .twe-scrollbar-thumb height in custom.css
  var THUMB_WIDTH  = 2;   // px — keep in sync with .twe-scrollbar-thumb width in custom.css
  var TRACK_INSET  = 40;  // px inset top & bottom, matches the CSS track margin
  var EDGE_GAP     = 2;   // px gap between thumb and the container's right edge
  var SELECTORS    = ['.grid__item--sidebar', '.drawer__scrollable'];

  function attach(container) {
    if (container.__tweScrollbar) return;
    container.__tweScrollbar = true;

    // A sidebar thumb must sit BELOW the sticky site header (z-index 28) so it
    // slides under it just like the sidebar's own content does. Without this it
    // paints over the header once `position: sticky` releases near the end of
    // the grid and the sidebar rides up the page. A drawer's thumb has the
    // opposite requirement: it must clear the drawer itself (z-index 30).
    var isDrawer = container.classList.contains('drawer__scrollable') ||
                   !!container.closest('.drawer');

    var thumb = document.createElement('div');
    thumb.className = 'twe-scrollbar-thumb' + (isDrawer ? ' twe-scrollbar-thumb--drawer' : '');
    thumb.style.display = 'none';
    document.body.appendChild(thumb);
    container.__tweThumb = thumb;

    var frame = null;

    function metrics() {
      var scrollable = container.scrollHeight - container.clientHeight;
      var trackH     = container.clientHeight - TRACK_INSET * 2;
      return { scrollable: scrollable, trackH: trackH, maxTop: trackH - THUMB_HEIGHT };
    }

    function hide() {
      thumb.style.display = 'none';
      container.classList.remove('twe-has-custom-scrollbar');
    }

    function render() {
      frame = null;

      var m  = metrics();
      var cr = container.getBoundingClientRect();

      // Nothing to scroll, container too short to host the thumb, container
      // hidden (closed drawer), or scrolled out of view.
      if (m.scrollable <= 0 || m.maxTop <= 0 || cr.height === 0 ||
          cr.bottom <= 0 || cr.top >= window.innerHeight) {
        hide();
        return;
      }

      thumb.style.display = '';
      container.classList.add('twe-has-custom-scrollbar');

      var progress = container.scrollTop / m.scrollable;
      var x = cr.right - THUMB_WIDTH - EDGE_GAP;
      var y = cr.top + TRACK_INSET + progress * m.maxTop;
      thumb.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    }

    // Coalesce every trigger into at most one paint-aligned update.
    function update() {
      if (frame === null) frame = window.requestAnimationFrame(render);
    }

    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // requestAnimationFrame does not run while the tab is hidden, so content
    // that changed in the background can leave the thumb stale.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) render();
    });

    // Filter groups expand/collapse and results re-render, both of which change
    // scrollHeight without firing a scroll event.
    if (window.MutationObserver) {
      new MutationObserver(update).observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    // ── drag ────────────────────────────────────────────────────────────────
    // Pointer events with capture, not mouse events: capture guarantees the
    // release still reaches us if the cursor leaves the window mid-drag.
    // Without it `dragging` and the body's `user-select: none` can stick on,
    // which silently breaks text selection across the whole site.
    var dragging = false, startY = 0, startScroll = 0;

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      thumb.classList.remove('is-dragging');
      document.body.style.userSelect = '';
    }

    thumb.addEventListener('pointerdown', function (e) {
      dragging    = true;
      startY      = e.clientY;
      startScroll = container.scrollTop;
      thumb.classList.add('is-dragging');
      document.body.style.userSelect = 'none';
      if (thumb.setPointerCapture) {
        try { thumb.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
      e.stopPropagation();
    });

    thumb.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      if (e.buttons === 0) { endDrag(); return; } // we missed the release
      var m = metrics();
      if (m.maxTop <= 0) return;
      container.scrollTop = startScroll + ((e.clientY - startY) / m.maxTop) * m.scrollable;
      // Assigning scrollTop does not reliably emit a scroll event, so the
      // scroll listener cannot be trusted to reposition the thumb mid-drag.
      render();
    });

    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);
    window.addEventListener('blur', endDrag);

    render();
  }

  function scan() {
    SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(attach);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  // The filter app builds its UI after fetching its data file.
  window.addEventListener('load', scan);
  document.addEventListener('twe:filters-synced', scan);
})();
